<template>
  <div ref="wrapRef" class="wd-relative">
    <v-text-field
      ref="textFieldRef"
      :model-value="phoneNumber"
      type="tel"
      :placeholder="placeholder"
      :error-messages="errorMessages"
      color="orange50"
      persistent-hint
      variant="outlined"
      density="compact"
      hide-details="auto"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
      @update:model-value="onPhoneChange($event)"
    >
      <template #prepend-inner>
        <!-- TODO: 驗證 offset 12,8 or 8,12 -->
        <v-menu
          v-model="isMenuOpen"
          :offset="[8, 12]"
          :close-on-content-click="false"
          :persistent="!!breakpoints.smAndDown"
          @update:model-value="onMenuOpen"
        >
          <template #activator="{ props }">
            <v-btn class="!wd-px-1" height="24" variant="text" v-bind="props">
              <template v-if="countryCode">
                <FlagIcon :country-code="countryCode" :width="16" :size="27" />
                <span class="wd-mr-1 wd-tracking-normal wd-text-base">{{ callingCode }}</span>
              </template>
              <div v-else class="wd-w-full wd-text-gray60 wd-text-base wd-mr-1">{{ $t('phone_country_code_search_title') }}</div>
              <FunNowSvgIcon
                max-width="16"
                image-path="chevron-down.svg"
                class="wd-transform wd-transition"
                :class="isMenuOpen ? 'wd-rotate-180' : ''"
              />
            </v-btn>
          </template>
          <v-card v-if="!breakpoints.smAndDown">
            <div class="wd-px-4 wd-pt-4">
              <v-text-field
                v-model="search"
                color="orange50"
                variant="outlined"
                density="compact"
                hide-details
                :placeholder="$t('phone_country_code_search_placeholder')"
              >
                <template #prepend-inner>
                  <FunNowSvgIcon max-width="20" image-path="magnify-icon_black.svg" />
                </template>
                <template #append>
                  <v-btn
                    v-show="search"
                    class="clear-btn pa-1"
                    min-width="24"
                    width="24"
                    height="24"
                    elevation="0"
                    variant="text"
                    @click="search = ''"
                  >
                    <FunNowSvgIcon max-width="16" image-path="remove-icon_gray.svg" />
                  </v-btn>
                </template>
              </v-text-field>
            </div>
            <!-- TODO: 驗證 flat 差異 Remove the highlighted background on active v-list-items -->
            <v-list :width="wrapWidth">
              <div v-show="!searchedOptions.length" class="wd-absolute wd-px-5 wd-py-3">
                <span class="wd-font-normal wd-text-sm wd-text-gray80">{{ $t('phone_country_code_no_search_results') }}</span>
              </div>
              <v-virtual-scroll ref="virtualScrollRef" :bench="2" :items="searchedOptions" height="280" item-height="52">
                <template #default="{ item, index }">
                  <v-list-item
                    :key="item.id"
                    :value="item.id"
                    :active="item.id === selectedOptionId"
                    color="orange50"
                    density="compact"
                    @click="onCallingCodeSelect(item, index)"
                  >
                    <v-list-item-title class="wd-px-1 wd-my-3">
                      <FlagIcon :country-code="item.value" :width="16" />
                      <span class="wd-text-base wd-font-normal">
                        <span class="wd-mr-2">{{ item.name }}</span>
                        <span>{{ item.callingCode }}</span>
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-virtual-scroll>
            </v-list>
          </v-card>
        </v-menu>
      </template>
    </v-text-field>
    <modal-pure-hint
      v-if="breakpoints.smAndDown"
      v-model="isModalOpen"
      :title="$t('phone_country_code_search_title')"
      width="100%"
      @close="isMenuOpen = false"
    >
      <div class="wd-relative">
        <v-card>
          <div class="wd-px-4 wd-pt-4 wd-sticky wd-top-0 wd-bg-white wd-z-10">
            <v-text-field
              v-model="search"
              color="orange50"
              variant="outlined"
              density="compact"
              hide-details
              :placeholder="$t('phone_country_code_search_placeholder')"
            >
              <template #prepend-inner>
                <FunNowSvgIcon max-width="20" image-path="magnify-icon_black.svg" />
              </template>
              <template #append>
                <v-btn v-show="search" class="clear-btn pa-1" min-width="24" width="24" height="24" elevation="0" variant="text" @click="search = ''">
                  <FunNowSvgIcon max-width="16" image-path="remove-icon_gray.svg" />
                </v-btn>
              </template>
            </v-text-field>
          </div>
          <div v-show="!searchedOptions.length" class="wd-absolute wd-px-5 wd-py-3">
            <span class="wd-font-normal wd-text-sm wd-text-gray80">{{ $t('phone_country_code_no_search_results') }}</span>
          </div>
          <!-- TODO: 驗證 flat UI -->
          <v-list class="wd-h-70vh wd-z-0">
            <v-virtual-scroll ref="virtualScrollRef" :bench="20" :items="searchedOptions" item-height="52">
              <template #default="{ item, index }">
                <v-list-item
                  :key="item.id"
                  :value="item.id"
                  :active="item.id === selectedOptionId"
                  color="orange50"
                  density="compact"
                  @click="onCallingCodeSelect(item, index)"
                >
                  <v-list-item-title class="wd-px-1 wd-my-3">
                    <FlagIcon :country-code="item.value" :width="16" />
                    <span class="wd-text-base wd-font-normal">
                      <span class="wd-mr-2">{{ item.name }}</span>
                      <span>{{ item.callingCode }}</span>
                    </span>
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-list>
        </v-card>
      </div>
    </modal-pure-hint>
  </div>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue'
import { useElementSize, useVModel, watchOnce } from '@vueuse/core'
import { VVirtualScroll, VTextField } from 'vuetify/components'
import { phoneHelpers } from '@/utils/phone'
import { useRegionNames } from '@/composition/useIntl'
import { sleep } from '@/utils/utils'

import FlagIcon from '@/components/FlagIcon.vue'
import FunNowSvgIcon from '@/components/icons/funnow-svg-icon.vue'
import ModalPureHint from '@/components/ModalPureHint.vue'
import { useBreakpoints } from '@/composition/useBreakpoints'

export default defineComponent({
  components: {
    FlagIcon,
    FunNowSvgIcon,
    ModalPureHint,
  },
  props: {
    modelValue: { type: String },
    placeholder: { type: String, default: '' },
    options: { type: Array as PropType<{ id: number; value: string }[]>, default: () => [] },
    optionIndex: { type: Number },
    errorMessages: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'blur', 'focus'],
  setup(props, ctx) {
    const { getRegionName } = useRegionNames()
    const { breakpoints } = useBreakpoints()
    const isModalOpen = ref(false)

    const search = ref('')

    /**
     * * MEMOS:
     * 1. templateRef + useElementSize + vue2.6.x 會遇到瘋狂 rerender 的 bug
     * 2. @vueuse/core v9.3 才有給 templateRef 增加 type `Component`，在那之前無法用 templateRef bind custom component (ex: Vuetify component)
     * 3. @vueuse/core v9 以上有 breaking change, ex: useClamp 移至 @vueuse/math
     */
    const wrapRef = ref<HTMLElement | null>(null)
    const { width: wrapWidth } = useElementSize(wrapRef)
    const virtualScrollRef = ref<InstanceType<typeof VVirtualScroll> | null>(null)
    const textFieldRef = ref<InstanceType<typeof VTextField> | HTMLInputElement | null>(null)
    const isMenuOpen = ref(false)

    /** 國碼選單
     * @ref https://github.com/funnow-docs/funnow-api-doc/blob/9ceb9a7703352c3e99a1ffe5ddb91836acce58b3/docs/product/booking_remark.md
     */
    const callingCodeOptions = computed(() =>
      props.options
        .map(option => ({
          ...option,
          callingCode: '+' + phoneHelpers.getCountryCodeForRegion(option.value),
          name: getRegionName(option.value),
        }))
        /**
         * 目前世界上沒有「+0」
         * 若出現代表 country code 設錯了
         * ex. 英國用了「UK」，正確是用「GB」
         */
        .filter(option => option.callingCode !== '+0')
    )
    const searchedOptions = computed(() =>
      callingCodeOptions.value.filter(
        option => option.name?.toLowerCase().includes(search.value?.toLowerCase()) || option.callingCode.includes(search.value)
      )
    )

    const selectedIndex = useVModel(props, 'optionIndex', ctx.emit, { defaultValue: 0 }) as Ref<number>
    const selectedOptionId = computed(() => callingCodeOptions.value[selectedIndex.value]?.id)
    const selectedOption = computed(() => callingCodeOptions.value[selectedIndex.value])
    /** 被選取的電話國碼 */
    const callingCode = computed(() => selectedOption?.value?.callingCode)
    /** 被選取的國家代碼 */
    const countryCode = computed(() => selectedOption?.value?.value)

    /** 手機號碼 */
    const phoneNumber = computed({
      get: () => props.modelValue?.split?.(' ').pop() || '',
      set: val => {
        const value = val?.split?.(' ').pop() || val || ''
        ctx.emit('update:modelValue', value ? `${callingCode.value} ${value}` : '')
      },
    })

    watch(callingCode, val => {
      if (phoneNumber.value) {
        ctx.emit('update:modelValue', `${val} ${phoneNumber.value}`)
      }
    })

    watchOnce(phoneNumber, val => {
      ctx.emit('update:modelValue', val ? `${callingCode.value} ${val}` : '')
    })

    async function onMenuOpen(isOpen: boolean) {
      isModalOpen.value = isOpen
      search.value = ''

      if (isOpen) {
        await sleep(200)
        virtualScrollRef.value?.$el?.scrollTo?.(0, selectedIndex.value * 40)
      }
    }

    async function onCallingCodeSelect(item: any, index: number) {
      selectedIndex.value = callingCodeOptions.value.findIndex(option => option.id === item.id) || index
      isMenuOpen.value = false
      isModalOpen.value = false
      search.value = ''
      await sleep(200)
      ;(textFieldRef.value as HTMLInputElement)?.focus?.()
      onPhoneChange(phoneNumber.value)
    }

    function onPhoneChange(value: string) {
      const valid = phoneHelpers.isValidNumber(value, selectedOption.value?.value)
      if (!valid) {
        phoneNumber.value = value
        return
      }
      const parsedNumber = phoneHelpers.toInternationalFormat(value, selectedOption.value?.value)
      phoneNumber.value = parsedNumber || value || ''
    }

    return {
      wrapRef,
      virtualScrollRef,
      textFieldRef,
      wrapWidth,
      phoneNumber,
      selectedOptionId,
      selectedOption,
      callingCode,
      countryCode,
      callingCodeOptions,
      searchedOptions,
      onMenuOpen,
      search,
      isMenuOpen,
      onCallingCodeSelect,
      isModalOpen,
      onPhoneChange,
      breakpoints,
    }
  },
})
</script>

<style>
/* extends virtual scroll item for item divider style */
.v-virtual-scroll__item {
  @apply wd-border-0 wd-border-solid not-last:wd-border-b-gray40 not-last:wd-border-b;
}
</style>
